import { lazy, Suspense, useState, useMemo, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import DescriptionPage from "../../components/Texts/DescriptionPage";
import IconButton from "../../components/Buttons/IconButton";
import Button from "../../components/Buttons/Button";
import { gallerySecondaryToolbarIconSx } from "../../components/Gallery/galleryIconButtonStyles";
import RangeDateField from "../../components/Form/RangeDateField";
import Select from "../../components/Form/Select";
import { optionsAdsCampaignBreakdownsReport } from "../../utils/options";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import { getPathDatabaseByRole } from "../../actions/auth";
import ModalModifyKpiCpa from "../AdminDashboard/components/ModalModifyKpiCpa";
import { useTranslationApp } from "../../lib/i18next";
import {
  FILTER_BY_DATES_PLACEHOLDER,
  GLOBAL_REPORT_DESCRIPTION,
  GLOBAL_REPORT_NO_CAMPAIGNS_FOR_KPI,
  GLOBAL_REPORT_NO_CAMPAIGNS_IN_PERIOD,
  GLOBAL_REPORT_NO_ACTIVITY_IN_PERIOD,
  GLOBAL_REPORT_OBJECTIVES_BUTTON,
  GLOBAL_REPORT_TITLE,
  GLOBAL_REPORT_ASK_QUESTION_OPEN,
  MODAL_CUSTOM_REPORT_CTA,
} from "../../i18n/keysTranslations";
import GlobalReportSummarySection from "./components/GlobalReportSummarySection";
import GlobalReportKpiRow from "./components/GlobalReportKpiRow";
import GlobalReportInsightsPanel from "./components/GlobalReportInsightsPanel";
import GlobalReportCampaignsTable from "./components/GlobalReportCampaignsTable";
import GlobalReportStatisticsSection from "./components/GlobalReportStatisticsSection";
import GlobalReportGoalsProgressSection from "./components/GlobalReportGoalsProgressSection";
import GlobalReportMeasurableKpiSelector from "./components/GlobalReportMeasurableKpiSelector";
import GlobalReportHeavyLoadPanel, {
  GLOBAL_REPORT_HEAVY_LOAD_MIN_CAMPAIGNS,
} from "./components/GlobalReportHeavyLoadPanel";
import ModalCustomReport from "../../components/Modal/ModalCustomReport";
import useGlobalReportData from "./hooks/useGlobalReportData";
import useGlobalReportStatistics from "./hooks/useGlobalReportStatistics";
import useGlobalReportDerivedMetrics from "./hooks/useGlobalReportDerivedMetrics";
import useGlobalReportAgentContext from "./hooks/useGlobalReportAgentContext";
import useGlobalReportKpiSummary from "./hooks/useGlobalReportKpiSummary";
import useGlobalReportSuggestedKpi from "./hooks/useGlobalReportSuggestedKpi";
import { isGlobalReportKpiGoalsConfigured } from "./utils/isGlobalReportKpiGoalsConfigured";
import { getPreviousDateRange } from "./utils/getPreviousDateRange";
import { useEnrichedGlobalReportCampaigns } from "./utils/enrichGlobalReportCampaigns";
import filterGlobalReportByMeasurableKpi from "./utils/filterGlobalReportByMeasurableKpi";
import { measurableKpiKeysMatch } from "./utils/globalReportMeasurableKpiRules";
import {
  BORDER_RADIUS_BUTTON,
  DATE_RANGE,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
} from "../../utils/constants";
import {
  getAdsCampaignDemoDateRange,
  shouldUseAdsCampaignDemoDateRange,
} from "../../utils/adsCampaignDemoDateRange";
import InlineExpandableChat from "../../components/CommonChat/InlineExpandableChat";
import {
  BOX_SHADOW_BUTTON,
  INPUT_COLOR_BORDER,
  PRIMARY_COLOR_TEXT,
} from "../../utils/colors";

/** Tabla de campañas: oculta temporalmente */
const SHOW_GLOBAL_REPORT_CAMPAIGNS_TABLE = false;

/** Descarga PDF: deshabilitada temporalmente (código conservado). */
const ENABLE_GLOBAL_REPORT_PDF_DOWNLOAD = false;
const GlobalReportPdfDownloadButton = ENABLE_GLOBAL_REPORT_PDF_DOWNLOAD
  ? lazy(() => import("./components/GlobalReportPdfDownloadButton"))
  : null;

/** Tooltips Recharts del reporte global (fondo blanco, alineado con previews de anuncios). */
const GLOBAL_REPORT_CHART_TOOLTIP_SX = {
  "& .recharts-tooltip-wrapper": {
    outline: "none",
    zIndex: 10,
  },
  "& .recharts-default-tooltip": {
    backgroundColor: "#FFFFFF !important",
    border: "1px solid #E5E5E5 !important",
    borderRadius: "12px !important",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18) !important",
  },
  "& .recharts-tooltip-wrapper .rounded-lg": {
    backgroundColor: "#FFFFFF !important",
  },
};

const GlobalReport = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
  const useDemoDateRange = shouldUseAdsCampaignDemoDateRange({ advertiserID });
  const demoDateRange = useMemo(() => getAdsCampaignDemoDateRange(), []);
  const [datePresetKey, setDatePresetKey] = useState(
    useDemoDateRange ? null : DATE_RANGE.last_7
  );
  const [isKpiModalOpen, setIsKpiModalOpen] = useState(false);
  const [isCustomReportModalOpen, setIsCustomReportModalOpen] = useState(false);
  const [hasUserSetMeasurableKpi, setHasUserSetMeasurableKpi] = useState(false);
  const [isStatisticsCaptureReady, setIsStatisticsCaptureReady] =
    useState(false);
  const [summaryAiSettled, setSummaryAiSettled] = useState(false);
  const [insightsAiSettled, setInsightsAiSettled] = useState(false);
  const [goalsContentSettled, setGoalsContentSettled] = useState(false);
  const [reportSummaryText, setReportSummaryText] = useState(null);
  const [reportInsights, setReportInsights] = useState(null);
  const skipMeasurableKpiChangeRef = useRef(false);
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      dateRange: useDemoDateRange
        ? {
            startDateTime: demoDateRange.startDateTime,
            endDateTime: demoDateRange.endDateTime,
          }
        : {
            startDateTime: null,
            endDateTime: null,
          },
      measurableKpi: null,
      breakdown: "weekly",
    },
  });
  const { dateRange, measurableKpi, breakdown } = watch();
  const { campaigns: candidateCampaigns, isLoadingCampaigns } =
    useGlobalReportData(dateRange);
  const candidateCampaignCount = useMemo(
    () => Object.keys(candidateCampaigns || {}).length,
    [candidateCampaigns]
  );
  const hasCandidateCampaigns = candidateCampaignCount > 0;
  const {
    statistics,
    previousPeriodStatistics,
    statisticsLoading,
    isLoadingStatistics,
    isLoadingCurrentStatistics,
    isLoadingAdsStatistics,
    isStatisticsSettledForRange,
    isPreviousPeriodSettledForRange,
    isAdsStatisticsSettledForRange,
    campaignsWithActivity,
  } = useGlobalReportStatistics({
    campaigns: candidateCampaigns,
    dateRange,
    isLoadingCampaigns,
  });

  const campaignsForReport = useMemo(
    () => (isStatisticsSettledForRange ? campaignsWithActivity : {}),
    [campaignsWithActivity, isStatisticsSettledForRange]
  );

  const showEmptyZeroCandidates =
    !isLoadingCampaigns &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    !hasCandidateCampaigns;

  const showEmptyNoActivity =
    !isLoadingCampaigns &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    hasCandidateCampaigns &&
    isStatisticsSettledForRange &&
    !isLoadingCurrentStatistics &&
    Object.keys(campaignsWithActivity || {}).length === 0;

  const showEmptyCampaignsState =
    showEmptyZeroCandidates || showEmptyNoActivity;

  /** Body/KPI readiness: first campaign stats fetch only (not previous/ads). */
  const isReportBodyLoading =
    isLoadingCampaigns ||
    (hasCandidateCampaigns &&
      (!isStatisticsSettledForRange || isLoadingCurrentStatistics));

  const { kpiSummary, isLoadingKpiSummary, onKpiSummarySaved } =
    useGlobalReportKpiSummary(advertiserID);

  const { isSuggestingKpi } = useGlobalReportSuggestedKpi({
    advertiserID,
    kpiSummary,
    isLoadingKpiSummary,
    hasUserSetMeasurableKpi,
    setValue,
    skipMeasurableKpiChangeRef,
    onKpiSummarySaved,
    skipAutoSuggest: useDemoDateRange,
  });

  useEffect(() => {
    if (
      useDemoDateRange ||
      hasUserSetMeasurableKpi ||
      !kpiSummary?.primaryKpi
    ) {
      return;
    }

    skipMeasurableKpiChangeRef.current = true;
    setValue("measurableKpi", kpiSummary.primaryKpi.trim(), {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [
    hasUserSetMeasurableKpi,
    kpiSummary?.primaryKpi,
    setValue,
    useDemoDateRange,
  ]);

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (name !== "measurableKpi" || type !== "change") {
        return;
      }

      if (skipMeasurableKpiChangeRef.current) {
        skipMeasurableKpiChangeRef.current = false;
        return;
      }

      setHasUserSetMeasurableKpi(true);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const {
    campaigns: reportCampaigns,
    statistics: reportStatistics,
    previousPeriodStatistics: reportPreviousStatistics,
  } = useMemo(
    () =>
      filterGlobalReportByMeasurableKpi({
        campaigns: campaignsForReport,
        statistics,
        previousPeriodStatistics,
        measurableKpiKey: measurableKpi,
      }),
    [campaignsForReport, measurableKpi, previousPeriodStatistics, statistics]
  );

  const hasReportCampaignsInRange = useMemo(
    () => Object.keys(reportCampaigns || {}).length > 0,
    [reportCampaigns]
  );

  const showEmptyFilteredKpiState =
    !isReportBodyLoading &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    Object.keys(campaignsForReport || {}).length > 0 &&
    !!measurableKpi &&
    !hasReportCampaignsInRange;

  const hasExplicitRange = !!(
    dateRange?.startDateTime && dateRange?.endDateTime
  );

  const { totals: reportTotals, totalsLastPeriod: reportTotalsLastPeriod } =
    useGlobalReportDerivedMetrics({
      campaigns: reportCampaigns,
      statistics: reportStatistics,
      previousPeriodStatistics: reportPreviousStatistics,
      dispatch,
      hasExplicitRange,
    });

  const enrichedReportCampaigns = useEnrichedGlobalReportCampaigns(
    reportCampaigns,
    reportStatistics
  );
  const isGoalsConfigured = useMemo(
    () => isGlobalReportKpiGoalsConfigured(kpiSummary),
    [kpiSummary]
  );
  const isGoalsContentVisible =
    isGoalsConfigured &&
    measurableKpiKeysMatch(measurableKpi, kpiSummary?.primaryKpi);

  const handleKpiSummarySaved = useCallback(
    (payload) => {
      onKpiSummarySaved(payload);

      if (useDemoDateRange) {
        return;
      }

      const nextPrimary = payload?.kpiSummary?.primaryKpi;
      if (typeof nextPrimary === "string" && nextPrimary.trim()) {
        skipMeasurableKpiChangeRef.current = true;
        setValue("measurableKpi", nextPrimary.trim());
      }
    },
    [onKpiSummarySaved, setValue, useDemoDateRange]
  );
  const { globalReportContext } = useGlobalReportAgentContext({
    campaigns: reportCampaigns,
    dateRange,
    totals: reportTotals,
    totalsLastPeriod: reportTotalsLastPeriod,
  });
  const hasStatisticsForAllCampaigns = useMemo(() => {
    const agentCampaignIDs = globalReportContext?.campaignIDs ?? [];
    if (agentCampaignIDs.length === 0) {
      return true;
    }
    return agentCampaignIDs.every(
      (campaignID) => reportStatistics?.campaigns?.[campaignID]
    );
  }, [globalReportContext?.campaignIDs, reportStatistics]);

  const agentRequestKey = useMemo(() => {
    if (!dateRange?.startDateTime || !dateRange?.endDateTime) {
      return null;
    }
    const campaignIDs = [...(globalReportContext?.campaignIDs || [])].sort();
    const measurableKey = measurableKpi || "all";
    return `${dateRange.startDateTime}|${dateRange.endDateTime}|${measurableKey}|${campaignIDs.join(",")}`;
  }, [
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    globalReportContext?.campaignIDs,
    measurableKpi,
  ]);

  useEffect(() => {
    setReportSummaryText(null);
    setReportInsights(null);
    setIsStatisticsCaptureReady(false);
    setSummaryAiSettled(false);
    setInsightsAiSettled(false);
    setGoalsContentSettled(false);
  }, [agentRequestKey]);

  const needsPreviousPeriod = useMemo(
    () =>
      !!(
        dateRange?.startDateTime &&
        dateRange?.endDateTime &&
        getPreviousDateRange(dateRange)
      ),
    // eslint-disable-next-line
    [dateRange?.endDateTime, dateRange?.startDateTime]
  );

  const hasPreviousPeriodData =
    Object.keys(reportPreviousStatistics?.campaigns || {}).length > 0;

  const hasComparisonData =
    !needsPreviousPeriod ||
    reportTotalsLastPeriod != null ||
    (globalReportContext?.campaignIDs?.length ?? 0) === 0 ||
    (isStatisticsSettledForRange && !hasPreviousPeriodData);

  const isAgentReady =
    !!globalReportContext &&
    !isLoadingCampaigns &&
    isStatisticsSettledForRange &&
    !isLoadingStatistics &&
    hasStatisticsForAllCampaigns &&
    hasComparisonData &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    hasReportCampaignsInRange;

  /**
   * Linear loader from the start of a range load.
   * After candidates resolve: keep it if ≥5, otherwise fall back to skeletons.
   */
  const showHeavyLoadUi =
    hasExplicitRange &&
    !showEmptyCampaignsState &&
    !showEmptyFilteredKpiState &&
    !(isAgentReady && summaryAiSettled && insightsAiSettled) &&
    (isLoadingCampaigns ||
      candidateCampaignCount >= GLOBAL_REPORT_HEAVY_LOAD_MIN_CAMPAIGNS);

  const heavyLoadResetKey = useMemo(() => {
    if (!dateRange?.startDateTime || !dateRange?.endDateTime) {
      return null;
    }
    // Stable for the range so progress does not reset when candidates arrive
    return `${dateRange.startDateTime}|${dateRange.endDateTime}`;
  }, [dateRange?.endDateTime, dateRange?.startDateTime]);

  const handleStatisticsCaptureReadyChange = useCallback((isReady) => {
    setIsStatisticsCaptureReady(isReady);
  }, []);

  const handleSummaryAiSettledChange = useCallback((isSettled) => {
    setSummaryAiSettled(isSettled);
  }, []);

  const handleInsightsAiSettledChange = useCallback((isSettled) => {
    setInsightsAiSettled(isSettled);
  }, []);

  const handleGoalsContentSettledChange = useCallback((isSettled) => {
    setGoalsContentSettled(isSettled);
  }, []);

  return (
    <ContainerPage hasMarginBottom={false} sx={{ height: "100%" }}>
      <Helmet>
        <title>{`${theme.nameBusiness || NAME_BUSINESS} - ${t(GLOBAL_REPORT_TITLE)}`}</title>
      </Helmet>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gridTemplateRows: { xs: "auto auto auto", md: "auto auto" },
          gridTemplateAreas: {
            xs: `"title"
                 "subtitle"
                 "actions"`,
            md: `"title actions"
                 "subtitle subtitle"`,
          },
          width: "100%",
          px: 3,
          mt: 4,
          gap: 1,
          alignItems: { xs: "flex-start", md: "center" },
        }}
      >
        <Box sx={{ gridArea: "title" }}>
          <TitlePage>{t(GLOBAL_REPORT_TITLE)}</TitlePage>
        </Box>
        <Box sx={{ gridArea: "subtitle", pb: { xs: 1, md: 0 } }}>
          <DescriptionPage>{t(GLOBAL_REPORT_DESCRIPTION)}</DescriptionPage>
        </Box>
        <Box
          sx={{
            gridArea: "actions",
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            width: { xs: "100%", md: "auto" },
            justifyContent: { xs: "flex-start", md: "flex-end" },
          }}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              ...gallerySecondaryToolbarIconSx({
                theme,
                buttonSize: 40,
                iconSvgFontSize: 20,
              }),
            })}
          >
            {ENABLE_GLOBAL_REPORT_PDF_DOWNLOAD &&
            GlobalReportPdfDownloadButton ? (
              <Suspense fallback={null}>
                <GlobalReportPdfDownloadButton
                  agentRequestKey={agentRequestKey}
                  businessName={theme.nameBusiness || NAME_BUSINESS}
                  campaigns={reportCampaigns}
                  dateRange={dateRange}
                  globalReportContext={globalReportContext}
                  goalsContentSettled={goalsContentSettled}
                  insights={reportInsights}
                  insightsAiSettled={insightsAiSettled}
                  isAgentReady={isAgentReady}
                  isGoalsConfigured={isGoalsConfigured}
                  isGoalsContentVisible={isGoalsContentVisible}
                  isStatisticsCaptureReady={isStatisticsCaptureReady}
                  kpiSummary={kpiSummary}
                  measurableKpi={measurableKpi}
                  statistics={reportStatistics}
                  summaryAiSettled={summaryAiSettled}
                  summaryText={reportSummaryText}
                  theme={theme}
                  totals={reportTotals}
                  totalsLastPeriod={reportTotalsLastPeriod}
                />
              </Suspense>
            ) : null}
            <IconButton
              onClick={() => setIsKpiModalOpen(true)}
              type="icon"
              edge={false}
              disabled={!advertiserID}
              tooltipText={GLOBAL_REPORT_OBJECTIVES_BUTTON}
            >
              <TrackChangesOutlinedIcon
                sx={{ color: PRIMARY_COLOR_TEXT, fill: PRIMARY_COLOR_TEXT }}
              />
            </IconButton>
          </Box>

          <RangeDateField
            showStartNowButton={false}
            defaultPlaceholderText={FILTER_BY_DATES_PLACEHOLDER}
            showClearButton={true}
            sx={{
              minWidth: "max-content",
              width: { xs: "100%", sm: "auto" },
              p: 1,
              borderRadius: BORDER_RADIUS_BUTTON,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              whiteSpace: "nowrap",
              height: 48,
              pl: 2,
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
            sxText={{
              fontSize: FONT_SIZE_BUTTON,
              lineHeight: 1,
              fontWeight: FONT_WEIGHT_BUTTON,
            }}
            startIcon={
              <CalendarTodayIcon
                sx={{ fontSize: 14, color: "text.secondary" }}
              />
            }
            control={control}
            name="dateRange"
            mode="text"
            onSave={() => {}}
            initialPresetKey={datePresetKey}
            onPresetChange={setDatePresetKey}
            showIconText={true}
          />

          <Select
            name="breakdown"
            variant="filled"
            fullWidth
            doTranslate={true}
            options={optionsAdsCampaignBreakdownsReport}
            control={control}
            errors={errors}
            rules={{ required: false }}
            label=""
            sx={{
              minWidth: { xs: "100%", sm: 160 },
              width: { xs: "100%", sm: "auto" },
              ".MuiInputBase-root": {
                height: 48,
              },
            }}
          />

          {!isSuggestingKpi ? (
            <GlobalReportMeasurableKpiSelector
              control={control}
              disabled={isLoadingCampaigns}
            />
          ) : null}

          <Button
            color="primary"
            onClick={() => setIsCustomReportModalOpen(true)}
            sx={{
              width: { xs: "100%", sm: "auto" },
              textTransform: "none",
            }}
          >
            {t(MODAL_CUSTOM_REPORT_CTA)}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          px: 3,
          pb: 3,
          mt: { xs: 3, md: 0 },
          ...GLOBAL_REPORT_CHART_TOOLTIP_SX,
        }}
      >
        {showEmptyCampaignsState ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: { xs: "50vh", md: "55vh" },
              mt: { xs: 4, md: 6 },
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 480, px: 2 }}
            >
              {t(
                showEmptyNoActivity
                  ? GLOBAL_REPORT_NO_ACTIVITY_IN_PERIOD
                  : GLOBAL_REPORT_NO_CAMPAIGNS_IN_PERIOD
              )}
            </Typography>
          </Box>
        ) : showEmptyFilteredKpiState ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: { xs: "50vh", md: "55vh" },
              mt: { xs: 4, md: 6 },
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 480, px: 2 }}
            >
              {t(GLOBAL_REPORT_NO_CAMPAIGNS_FOR_KPI)}
            </Typography>
          </Box>
        ) : hasExplicitRange ? (
          <>
            {showHeavyLoadUi ? (
              <GlobalReportHeavyLoadPanel resetKey={heavyLoadResetKey} />
            ) : null}
            <Box
              sx={
                showHeavyLoadUi
                  ? {
                      display: "none",
                    }
                  : {
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      width: "100%",
                    }
              }
              aria-hidden={showHeavyLoadUi}
            >
              <GlobalReportSummarySection
                globalReportContext={globalReportContext}
                agentRequestKey={agentRequestKey}
                isReady={isAgentReady}
                onSummaryGenerated={setReportSummaryText}
                onAiSettledChange={handleSummaryAiSettledChange}
              />
              {globalReportContext && isAgentReady && (
                <InlineExpandableChat
                  globalReportContext={globalReportContext}
                  agentFlow="globalReport"
                  placeholder={GLOBAL_REPORT_ASK_QUESTION_OPEN}
                  inputBarSx={(theme) => ({
                    backgroundColor: theme.palette.button.secondary.main,
                    border: "none",
                    boxShadow: BOX_SHADOW_BUTTON,
                  })}
                />
              )}
              <GlobalReportKpiRow
                totals={reportTotals}
                totalsLastPeriod={reportTotalsLastPeriod}
                isLoadingStatistics={isLoadingCurrentStatistics}
                isLoadingCampaigns={isLoadingCampaigns}
                isStatisticsSettledForRange={isStatisticsSettledForRange}
                isPreviousPeriodSettledForRange={isPreviousPeriodSettledForRange}
              />
              <GlobalReportInsightsPanel
                globalReportContext={globalReportContext}
                agentRequestKey={agentRequestKey}
                isReady={isAgentReady}
                onInsightsGenerated={setReportInsights}
                onAiSettledChange={handleInsightsAiSettledChange}
              />
              {SHOW_GLOBAL_REPORT_CAMPAIGNS_TABLE ? (
                <GlobalReportCampaignsTable
                  campaigns={enrichedReportCampaigns}
                  statisticsLoading={statisticsLoading}
                  isLoadingCampaigns={isLoadingCampaigns}
                />
              ) : null}
              <GlobalReportStatisticsSection
                campaigns={reportCampaigns}
                statistics={reportStatistics}
                breakdown={breakdown}
                isLoadingStatistics={isLoadingCurrentStatistics}
                isLoadingAdsStatistics={isLoadingAdsStatistics}
                isLoadingCampaigns={isLoadingCampaigns}
                isStatisticsSettledForRange={isStatisticsSettledForRange}
                isAdsStatisticsSettledForRange={isAdsStatisticsSettledForRange}
                onCaptureReadyChange={
                  ENABLE_GLOBAL_REPORT_PDF_DOWNLOAD
                    ? handleStatisticsCaptureReadyChange
                    : undefined
                }
              />
              <GlobalReportGoalsProgressSection
                kpiSummary={kpiSummary}
                campaigns={reportCampaigns}
                statistics={reportStatistics}
                dateRange={dateRange}
                breakdown={breakdown}
                isLoadingStatistics={isLoadingCurrentStatistics}
                isStatisticsSettledForRange={isStatisticsSettledForRange}
                isLoadingCampaigns={isLoadingCampaigns}
                isGoalsConfigured={isGoalsConfigured}
                isGoalsContentVisible={isGoalsContentVisible}
                onConfigureGoals={() => setIsKpiModalOpen(true)}
                onContentSettledChange={
                  ENABLE_GLOBAL_REPORT_PDF_DOWNLOAD
                    ? handleGoalsContentSettledChange
                    : undefined
                }
              />
            </Box>
          </>
        ) : null}
      </Box>

      {isKpiModalOpen && (
        <ModalModifyKpiCpa
          modalOpen={isKpiModalOpen}
          onCloseModal={() => setIsKpiModalOpen(false)}
          advertiserID={advertiserID}
          businessName={theme.nameBusiness || NAME_BUSINESS}
          onCallbackSave={handleKpiSummarySaved}
        />
      )}
      <ModalCustomReport
        modalOpen={isCustomReportModalOpen}
        onCloseModal={() => setIsCustomReportModalOpen(false)}
        dateRange={dateRange}
      />
    </ContainerPage>
  );
};

export default GlobalReport;
